import React from 'react';
import {useTranslation} from 'react-i18next';

import {languages, formatLanguageCode} from 'i18n/options';
import changeI18nLanguage from 'utils/i18n/changeLanguage';
import config from 'utils/config';

/* Actions */

import {useFetchUserProfile, useUpdateUserLanguage} from 'actions/userProfile';
import {useUpdateMyUserSettings} from 'actions/userSettings';

/* Components */

import Card from 'components/Card';
import Heading from 'components/Heading';
import PageSection from 'components/PageSection';
import Panel from 'components/Panel';
import FormField from 'components/FormField';
import CheckboxOption from 'components/CheckboxOption';
import RadioButtonOption from 'components/RadioButtonOption';
import EmptyStateController from 'components/EmptyStateController';
import Text from 'components/Text';
import {Select, Option} from 'components/Select';
import PageTitle from 'components/PageTitle';
import ProfileEditor from './editor';

const SECTION_WIDTH = 600;

function UserSettingsPage() {
	const {t, i18n} = useTranslation();
	const {userProfile, isLoading, isError} = useFetchUserProfile();

	const {updateUserLanguage} = useUpdateUserLanguage();
	function changeLanguage(lang) {
		updateUserLanguage(lang);
		changeI18nLanguage(lang);
	}

	const {updateUserSettings, isLoading: isUpdatingSettings} =
		useUpdateMyUserSettings();

	function updateSettings(key, value) {
		updateUserSettings({
			...userProfile?.settings,
			[key]: value,
		});
	}

	const isLangEnglish = i18n.language.substring(0, 2) === 'en';
	const selectedLanguage = formatLanguageCode(i18n.language);

	const notificationChannels = {
		all: {
			label: 'profile_settings_notifications_channel_all',
		},
		email: {
			label: 'profile_settings_notifications_channel_email',
		},
		ms_teams: {
			label: 'profile_settings_notifications_channel_ms_teams',
		},
	};

	const {hasMsTeamsId, enableComments: areCommentsEnabled} = config;

	return (
		<>
			<PageTitle visuallyHidden>{t('navbar_user_settings')}</PageTitle>
			<EmptyStateController isLoading={isLoading} isError={isError}>
				<PageSection maxWidth={SECTION_WIDTH}>
					<Card>
						<ProfileEditor
							initialFirstName={userProfile?.first_name ?? ''}
							initialLastName={userProfile?.last_name ?? ''}
							email={userProfile?.email}
							userCustomFields={userProfile?.userCustomFields}
						/>
					</Card>
				</PageSection>

				<PageSection maxWidth={SECTION_WIDTH}>
					<Card>
						<Panel>
							<Heading as="h2" id="select_language_title">
								{t('profile_choose_language')}
							</Heading>
							{!isLangEnglish && (
								<Text dimmed as="p" lang="en">
									{t('profile_choose_language', {lng: 'en'})}
								</Text>
							)}
						</Panel>
						<Panel>
							<Select
								id="profile_language_dropdown"
								labelledById="select_language_title"
								size="medium"
								value={selectedLanguage}
								onChange={changeLanguage}
							>
								{languages.map(lang => (
									<Option key={lang.value} value={lang.value}>
										{lang.title}
									</Option>
								))}
							</Select>
						</Panel>
					</Card>
				</PageSection>

				{(hasMsTeamsId || areCommentsEnabled) && (
					<PageSection maxWidth={SECTION_WIDTH}>
						<Card>
							<Panel>
								<Heading as="h2">
									{t('profile_settings_notifications')}
								</Heading>
							</Panel>
							{hasMsTeamsId && (
								<Panel>
									<FormField
										as="fieldset"
										label={t(
											'profile_settings_notifications_channel'
										)}
									>
										{Object.entries(
											notificationChannels
										).map(([channel, {label}]) => (
											<RadioButtonOption
												key={`notification_channel_${channel}_radiobttn`}
												label={t(label)}
												id={channel}
												disabled={isUpdatingSettings}
												value={channel}
												checked={
													channel ===
													userProfile?.settings
														?.notification_channel
												}
												onChange={event =>
													updateSettings(
														'notification_channel',
														event.target.value
													)
												}
											/>
										))}
									</FormField>
								</Panel>
							)}
							{areCommentsEnabled && (
								<Panel>
									<FormField
										as="fieldset"
										label={t(
											'profile_settings_notifications_comments'
										)}
									>
										<CheckboxOption
											label={t(
												'profile_settings_autosubscription'
											)}
											id="auto-subscription-toggle"
											disabled={isUpdatingSettings}
											checked={
												userProfile?.settings
													?.autoSubscribe
											}
											onChange={e =>
												updateSettings(
													'autoSubscribe',
													!!e.target.checked
												)
											}
										/>
									</FormField>
								</Panel>
							)}
						</Card>
					</PageSection>
				)}
			</EmptyStateController>
		</>
	);
}

export default UserSettingsPage;
